import { useEffect, useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import { ScheduleAppointment } from '../../../../../packages/ui/templates/sign-off/components/schedule-appointment';
import { btnType, Button, Icons } from '../../../../../packages';
import { colors } from '../../../../../packages';

import { getAppointments } from '../../../../../services/appointment/appointment.service';
import { profileActionCreators } from '../../../../../redux/modules/profile';
import { getProfile } from '../../../../../redux/modules/profile/selectors';
import { sx } from '../SessionSignOffV4.styles';

import UpdateVisitFrequency from '../../../member-detail/components/CareTeam/edit';
import AddSchedule from '../../../../admin/appointments/add-schedule';
import { appointmentActionCreators } from '../../../../../redux/modules/appointment';
import { selectProviderSchedule } from '../../../../../redux/modules/schedule/selectors';
import { getAuth } from '../../../../../redux/modules/auth/selectors';
import * as actionType from '../../../../../redux/modules/appointment/actions';
import { scheduleActionCreators } from '../../../../../redux/modules/schedule';

export const AppointmentSection = ({ appointment, openGiggle }) => {
  const dispatch = useDispatch();
  const { isAdmin } = useSelector(getAuth);
  const { profile, demographicDetails, isLoading } = useSelector(getProfile);
  const providerSchedule = useSelector(selectProviderSchedule);

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [schedulesDrawerOpen, setSchedulesDrawerOpen] = useState(false);
  const [openVisitFrequency, setOpenVisitFrequency] = useState(false);
  // pagination states
  const [totalAppointments, setTotalAppointments] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [value, setValue] = useState({
    search: { searchKey: '' },
    pagination: { currentPage: 1, rowsPerPage: 10 },
    sorter: { direction: 'asc', column: 'startTime' },
  });

  useEffect(() => {
    if (appointment?.participantId) {
      dispatch(profileActionCreators.fetchDemographicDetails(appointment?.participantId));
      setLoading(true);
      void fetchAppointments();
    }
  }, [appointment?.participantId]);

  const fetchAppointments = () => {
    const queryParams = {
      participantIds: appointment?.participantId,
      type: 'PENDING,CURRENT',
      page: value.pagination.currentPage - 1,
      size: value.pagination.rowsPerPage,
      orderBy: 'asc',
      sortBy: 'startTime',
      statuses: 'BOOKED,PROPOSED',
      searchQuery: value.search.searchKey || '',
    };

    if (!providerSchedule && !isAdmin) {
      dispatch(scheduleActionCreators.fetchProviderSchedule({ userId: profile.providerId }));
    }

    getAppointments(queryParams)
      .then(({ data }) => {
        dispatch({
          type: actionType.FETCH_APPOINTMENTS_SUCCESSFUL,
          payload: {
            appointments: data.results,
            currentPage: data?.page,
            totalPages: Math.ceil(data?.total / data?.size),
            total: data?.total,
            loadingMore: false,
          },
        });

        setAppointments(prev => {
          if (isLoadingMore) {
            return [...prev, ...data.results];
          }
          return data.results;
        });

        setTotalAppointments(data.total);
        setLoading(false);
        setIsLoadingMore(false);
      })
      .catch(err => {
        console.log({ err });
        setIsLoadingMore(false);
        setLoading(false);
      });
  };

  const onLoadMoreClick = () => {
    setIsLoadingMore(true);
    setValue(prev => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        currentPage: prev.pagination.currentPage + 1,
      },
    }));
  };

  useEffect(() => {
    if (appointment?.participantId) {
      fetchAppointments();
    }
  }, [value.pagination.currentPage]);

  const onSubmitNewSchedule = payload => {
    dispatch(
      appointmentActionCreators.createAppointment({
        data: { ...payload, timezone: providerSchedule?.timezone },
        callback: (isSucceed /* , isInstantSession: boolean, appointmentId: string */) => {
          if (isSucceed) {
            setSchedulesDrawerOpen(false);
            fetchAppointments();
          }
        },
      }),
    );
  };

  const handleScheduleClick = () => {
    setSchedulesDrawerOpen(true);
  };

  const frequencyDataObj = useMemo(() => {
    return {
      userId: profile?.providerId,
      name: profile?.fullName,
      visitFrequency: demographicDetails?.visitFrequency,
    };
  }, [demographicDetails?.visitFrequency, profile?.providerId, profile?.fullName]);

  return (
    <Box display="flex" flexDirection="column" gap="40px">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: 28,
            lineHeight: '36px',
            color: colors.neutral900,
          }}
        >
          Upcoming appointments
        </Typography>
        <span className={{ color: colors.neutral600, fontWeight: 500, fontSize: '16px', lineHeight: '24px' }}>
          Always schedule in session. It's best to discuss the next appointment with the patient.
        </span>
      </Box>
      {loading || isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
        </Box>
      ) : (
        <ScheduleAppointment
          value={value}
          appointments={appointments}
          totalAppointments={totalAppointments}
          isLoadingMore={isLoadingMore}
          visitFrequency={demographicDetails?.visitFrequency}
          onSchedule={handleScheduleClick}
          onUpdateVisitFrequency={() => setOpenVisitFrequency(true)}
          onLoadMoreClick={onLoadMoreClick}
        />
      )}

      {appointments.length !== 0 && (
        <Box
          sx={{
            padding: '40px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <Typography
            sx={{
              color: colors.neutral900,
              fontFamily: 'Manrope',
              fontWeight: 500,
              fontSize: 14,
              lineHeight: '20px',
            }}
          >
            Want to meet with this patient regularly?
          </Typography>
          <Button
            sx={{
              textTransform: 'none !important',
              fontWeight: 700,
              fontSize: 14,
              lineHeight: '20px',
              color: colors.primary500,
              maxHeight: '20px',
            }}
            variant={btnType.TEXT}
            onClick={handleScheduleClick}
          >
            Create Recurring Appointment
          </Button>
        </Box>
      )}

      {/* Drawers */}
      <UpdateVisitFrequency
        openDrawer={openVisitFrequency}
        onClose={() => setOpenVisitFrequency(false)}
        data={frequencyDataObj}
        currentUserId={appointment?.participantId}
      />
      {schedulesDrawerOpen && (
        <AddSchedule
          isOpen={schedulesDrawerOpen}
          onClose={() => setSchedulesDrawerOpen(false)}
          onSubmit={onSubmitNewSchedule}
          memberId={appointment?.participantId}
        />
      )}
    </Box>
  );
};
